import { useState, useEffect, useMemo } from "react";
import { useQueryParams } from "@certn/api";
interface UseTabStateOptions<T extends readonly string[]> {
  validTabs: T;
  defaultTab: T[number];
  paramName?: string;
}

export function useTabState<T extends readonly string[]>({
  validTabs,
  defaultTab,
  paramName = "tab",
}: UseTabStateOptions<T>) {
  const { params, setParam } = useQueryParams();

  const getInitialTab = () => {
    const urlTab = Array.isArray(params[paramName])
      ? params[paramName][0]
      : params[paramName];
    return validTabs.includes(urlTab) ? urlTab : defaultTab;
  };

  const [selectedKey, setSelectedKey] = useState(() => getInitialTab());

  const handleSelectionChange = (key: React.Key) => {
    const tabKey = key as T[number];
    setSelectedKey(tabKey);
    setParam(paramName, tabKey);
  };
  const validTabsKey = validTabs.join("|");
  // We intentionally depend on 'validTabsKey' to avoid unnecessary re-renders when the array reference changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validTabsSet = useMemo(() => new Set(validTabs), [validTabsKey]);

  useEffect(() => {
    const urlTabParam = params[paramName];
    const urlTab = Array.isArray(urlTabParam) ? urlTabParam[0] : urlTabParam;
    const validTab = validTabsSet.has(urlTab) ? urlTab : defaultTab;
    if (validTab !== selectedKey) {
      setSelectedKey(validTab);
    }
  }, [params, paramName, selectedKey, defaultTab, validTabsSet]);

  return {
    selectedKey,
    handleSelectionChange,
  };
}
